import { getCartErrorMessage } from '@theme/cart-feedback';

// <s-wings-builder> switches Wings/WingBites panels, counts ranch (first one free,
// extras added as the ranch product), keeps the live total, and adds everything in one update.
const MAX_RANCH = 10;

class SWingsBuilder extends HTMLElement {
  #ac;
  #ranch = 1;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.addEventListener('change', () => this.#sync(), { signal });
    this.addEventListener('command', (event) => {
      if (event.command === '--ranch-less') this.#setRanch(this.#ranch - 1);
      if (event.command === '--ranch-more') this.#setRanch(this.#ranch + 1);
      if (event.command === '--add-wings') this.#add(event.source || this.querySelector('[data-wings-add]'));
    }, { signal });
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#bakeTimer);
  }

  #bakeTimer;
  #bake() {
    clearTimeout(this.#bakeTimer);
    delete this.dataset.baked;
    void this.offsetWidth; // restart CSS animations on repeat adds
    this.dataset.baked = 'true';
    const status = this.querySelector('[data-wings-status]');
    if (status && this.dataset.msgOven) status.textContent = this.dataset.msgOven;
    this.#bakeTimer = setTimeout(() => delete this.dataset.baked, 2600);
  }

  get #ranchVariant() {
    return this.dataset.ranchVariant || '';
  }

  get #ranchPrice() {
    return Number(this.dataset.ranchPrice || 0);
  }

  #money(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
      }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  #panel() {
    const type = this.querySelector('input[data-wing-type]:checked')?.value || 'wings';
    return this.querySelector(`[data-wing-panel="${type}"]`);
  }

  #setRanch(value) {
    const max = this.#ranchVariant ? MAX_RANCH : 1;
    this.#ranch = Math.max(0, Math.min(max, value));
    this.#sync();
  }

  #sync() {
    const type = this.querySelector('input[data-wing-type]:checked')?.value || 'wings';
    this.querySelectorAll('[data-wing-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.wingPanel !== type;
    });

    const extra = Math.max(0, this.#ranch - 1);
    const count = this.querySelector('[data-ranch-count]');
    if (count) count.textContent = String(this.#ranch);
    const less = this.querySelector('[command="--ranch-less"]');
    if (less) less.disabled = this.#ranch <= 0;
    const more = this.querySelector('[command="--ranch-more"]');
    if (more) more.disabled = !this.#ranchVariant || this.#ranch >= MAX_RANCH;

    const note = this.querySelector('[data-ranch-note]');
    if (note) {
      note.textContent = extra > 0
        ? (this.dataset.ranchExtra || '').replace('[count]', extra).replace('[price]', this.#money(extra * this.#ranchPrice))
        : this.dataset.ranchFree || '';
    }

    // Photo preview: basket photo per type + flavor, bake tint, ranch cups beside the basket.
    const flavorKey = this.#panel()?.querySelector('input[data-wing-flavor]:checked')?.dataset.wingFlavor;
    if (flavorKey) this.dataset.photo = `${type}-${flavorKey}`;
    this.dataset.bake = this.querySelector('input[data-wing-bake]:checked')?.dataset.wingBake === 'extra' ? 'extra' : 'regular';
    const cups = this.querySelectorAll('[data-ranch-cup]');
    cups.forEach((cup) => {
      cup.dataset.on = String(Number(cup.dataset.ranchCup) <= this.#ranch);
    });
    const moreChip = this.querySelector('[data-ranch-more-chip]');
    if (moreChip) {
      const hiddenCups = cups.length ? this.#ranch - cups.length : 0;
      moreChip.hidden = hiddenCups <= 0;
      if (hiddenCups > 0) moreChip.textContent = (this.dataset.ranchMore || '').replace('[count]', hiddenCups);
    }

    const size = this.#panel()?.querySelector('input[data-wing-size]:checked');
    const chip = this.querySelector('[data-wings-chip]');
    if (chip) {
      const sizeName = size?.closest('label')?.querySelector('.font-semibold')?.textContent.trim() || '';
      const flavorName = this.#panel()?.querySelector('input[data-wing-flavor]:checked')?.value || '';
      chip.textContent = [flavorName, sizeName].filter(Boolean).join(' · ');
      chip.hidden = !chip.textContent;
    }
    const label = this.querySelector('[data-wings-add-label]');
    const button = this.querySelector('[data-wings-add]');
    if (button) button.disabled = !size;
    if (label && size) {
      const total = Number(size.dataset.price || 0) + extra * this.#ranchPrice;
      label.textContent = (this.dataset.addTemplate || '').replace('[price]', this.#money(total));
    }
  }

  async #add(button) {
    if (!(button instanceof HTMLButtonElement) || button.getAttribute('aria-busy') === 'true') return;
    const panel = this.#panel();
    const size = panel?.querySelector('input[data-wing-size]:checked');
    if (!size?.value) return;

    const flavor = panel.querySelector('input[data-wing-flavor]:checked')?.value || '';
    const bake = this.querySelector('input[data-wing-bake]:checked')?.value || '';
    const attributes = [
      { key: this.dataset.propertyFlavor, value: flavor },
      { key: this.dataset.propertyBake, value: bake },
      { key: this.dataset.propertyRanch, value: String(this.#ranch) },
    ].filter((a) => a.key && a.value);

    const lines = [{ merchandiseId: size.value, quantity: 1, attributes }];
    const extra = Math.max(0, this.#ranch - 1);
    if (extra > 0 && this.#ranchVariant) lines.push({ merchandiseId: this.#ranchVariant, quantity: extra });

    const error = this.querySelector('[data-wings-error]');
    if (error) error.hidden = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('opacity-70');

    try {
      if (!window.Shopify?.actions?.updateCart) throw new Error('unavailable');
      const result = await window.Shopify.actions.updateCart({ lines }, { event: { context: 'product' } });
      const message = result?.userErrors?.[0]?.message;
      if (message) throw message;
      // Fresh-out-the-fryer moment, then open the cart so buyers get to see it.
      this.#bake();
      await new Promise((resolve) => setTimeout(resolve, 1900));
      await window.Shopify.actions.openCart?.();
    } catch (err) {
      if (error) {
        error.textContent = (typeof err === 'string' ? err : getCartErrorMessage(err)) || this.dataset.errorMessage || '';
        error.hidden = false;
      }
    } finally {
      button.removeAttribute('aria-busy');
      button.classList.remove('opacity-70');
    }
  }
}

if (!customElements.get('s-wings-builder')) customElements.define('s-wings-builder', SWingsBuilder);
